<script setup>
import { ref, onMounted, reactive } from "vue";
import { loadGoogleMaps } from "@/utils/loadGoogleMaps";

const props = defineProps({
  LoteStore: Object,
});

const map = ref(null);
const marker = ref(null);
const mapVisible = ref(false);
const subasta_ubicacion = reactive(props.LoteStore.lote?.subasta_ubicacion || null);
const apiKey = process.env.VUE_APP_API_KEY_GMAPS;
const mapImageUrl = ref(null);

// Geocodificación
const geocodeAddress = async (address) => {
  return new Promise((resolve, reject) => {
    loadGoogleMaps(apiKey)
      .then(() => {
        if (!window.google || !window.google.maps) return reject("API no cargada");
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results[0]) {
            const location = results[0].geometry.location;
            resolve({ lat: location.lat(), lng: location.lng() });
          } else {
            reject(status);
          }
        });
      })
      .catch(reject);
  });
};

// Imagen estática
const generarImagenMapa = async () => {
  if (subasta_ubicacion?.lat && subasta_ubicacion?.lng) {
    const lat = parseFloat(subasta_ubicacion.lat);
    const lng = parseFloat(subasta_ubicacion.lng);
    mapImageUrl.value = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=1050x400&scale=2&maptype=roadmap&markers=color:red%7C${lat},${lng}&key=${apiKey}`;
  } else if (subasta_ubicacion?.direccion) {
    const direccionCompleta = `${subasta_ubicacion.direccion}, ${subasta_ubicacion.ciudad}, ${subasta_ubicacion?.provincia?.nombre}, ${subasta_ubicacion?.pais?.nombre}`;
    const { lat, lng } = await geocodeAddress(direccionCompleta);
    mapImageUrl.value = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=1050x400&scale=2&maptype=roadmap&markers=color:red%7C${lat},${lng}&key=${apiKey}`;
  }
};

// Mapa dinámico
const initMap = async (lat, lng) => {
  try {
    await loadGoogleMaps(apiKey);
    if (!window.google || !window.google.maps) return;

    map.value = new window.google.maps.Map(document.getElementById("map"), {
      center: { lat, lng },
      zoom: 15,
    });

    marker.value = new window.google.maps.Marker({
      position: { lat, lng },
      map: map.value,
      title: "Ubicación seleccionada",
    });
  } catch (error) {
    console.error("Error al inicializar el mapa:", error);
  }
};

// Mostrar mapa dinámico al hacer clic
const activarMapaInteractivo = async () => {
  mapVisible.value = true;

  let lat = null;
  let lng = null;

  if (subasta_ubicacion?.lat && subasta_ubicacion?.lng) {
    lat = parseFloat(subasta_ubicacion.lat);
    lng = parseFloat(subasta_ubicacion.lng);
  } else if (subasta_ubicacion?.direccion) {
    const direccionCompleta = `${subasta_ubicacion.direccion}, ${subasta_ubicacion.ciudad}, ${subasta_ubicacion?.provincia?.nombre}, ${subasta_ubicacion?.pais?.nombre}`;
    const coords = await geocodeAddress(direccionCompleta);
    lat = coords.lat;
    lng = coords.lng;
  }

  if (lat && lng) {
    await initMap(lat, lng);
  }
};

onMounted(() => {
  generarImagenMapa();
});
</script>

<template>
  <div class="card">
    <div class="card-body">
      <div class="tab-content mt-1">
        <div
          class="tab-pane fade show active"
          v-if="props.LoteStore.lote?.subasta_ubicacion_id"
          id="ubicacion"
          role="tabpanel"
          aria-labelledby="ubicacion-tab"
        >
          <div class="d-flex align-items-start">
            <i class="fa fa-location-dot mt-1 me-2 text-dark"></i>
            <div>
              <p class="mb-1">
                <strong>Ubicación:</strong>
                {{ subasta_ubicacion?.direccion ? `${subasta_ubicacion?.direccion}, ` : "" }}
                {{ subasta_ubicacion?.ciudad }}
                {{ subasta_ubicacion?.provincia ? `, ${subasta_ubicacion?.provincia?.nombre}` : "" }}
                {{ subasta_ubicacion?.pais ? `, ${subasta_ubicacion?.pais?.nombre}` : "" }}
              </p>
            </div>
          </div>

          <div class="d-flex align-items-start" v-if="subasta_ubicacion?.se_exhibe && subasta_ubicacion.horarios">
            <i class="fa fa-clock mt-1 me-2 text-dark"></i>
            <p><strong>Fecha y horario de exhibición:</strong> {{ subasta_ubicacion?.horarios }}</p>
          </div>

          <div class="d-flex align-items-start" v-if="subasta_ubicacion?.se_exhibe && subasta_ubicacion.info">
            <i class="fa fa-envelope mt-1 me-2 text-dark"></i>
            <p><strong>Datos de contacto:</strong> {{ subasta_ubicacion.info || "No hay contacto registrado." }}</p>
          </div>

          <div class="map-container mt-4">
            <!-- Imagen previa -->
            <img
              v-if="!mapVisible && mapImageUrl"
              :src="mapImageUrl"
              @click="activarMapaInteractivo"
              class="map-image rounded"
              style="cursor: pointer;"
              alt="Vista previa del mapa"
            />

            <!-- Mapa dinámico -->
            <div v-else id="map" class="map rounded"></div>
          </div>
        </div>

        <div class="tab-pane fade show active" v-else id="ubicacion" role="tabpanel" aria-labelledby="ubicacion-tab">
          <p>No hay una ubicación cargada</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.map-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.map {
  width: 100%;
  height: 400px;
  border: 1px solid #ccc;
  margin-bottom: 20px;
}

.map-image {
  width: 100%;
  height: auto;
  max-height: 400px;
  border: 1px solid #ccc;
  object-fit: cover;
  margin-bottom: 20px;
}
</style>
